import axios from "axios";

const productUrl = "/v2/api/products";

class ProductServiceV2 {
    // Product operations
    static searchProducts(urlParams) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}${urlParams}`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static getProductById(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static getProductByCode(productCode) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/by-code/${productCode}`;
                const result = await axios.get(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static createProduct(productData) {
        return new Promise(async (resolve, reject) => {
            try {
                const result = await axios.post(productUrl, productData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static generateProductCode() {
        return new Promise(async (resolve, reject) => {
            try {
                const result = await axios.post(`${productUrl}/generate-code`);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateProduct(productId, updatedProductData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}`;
                await axios.put(url, updatedProductData);
                resolve(`Product with ID ${productId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}`;
                await axios.delete(url);
                resolve(`Product with ID ${productId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static verifyProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/verify`;
                await axios.put(url);
                resolve(`Product with ID ${productId} verified successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static archiveProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/archive`;
                await axios.put(url);
                resolve(`Product with ID ${productId} archived successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Line Item operations
    static createLineItem(productId, lineItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/line-items`;
                const result = await axios.post(url, lineItemData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateLineItem(productId, lineItemId, updatedLineItemData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/line-items/${lineItemId}`;
                await axios.put(url, updatedLineItemData);
                resolve(`Line item with ID ${lineItemId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteLineItem(productId, lineItemId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/line-items/${lineItemId}`;
                await axios.delete(url);
                resolve(`Line item with ID ${lineItemId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Price Band operations
    static createPriceBand(productId, priceBandData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/price-bands`;
                const result = await axios.post(url, priceBandData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updatePriceBand(productId, priceBandId, updatedPriceBandData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/price-bands/${priceBandId}`;
                await axios.put(url, updatedPriceBandData);
                resolve(`Price band with ID ${priceBandId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deletePriceBand(productId, priceBandId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/price-bands/${priceBandId}`;
                await axios.delete(url);
                resolve(`Price band with ID ${priceBandId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Document operations
    static uploadDocument(productId, documentData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/documents`;
                const result = await axios.post(url, documentData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteDocument(productId, documentId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/documents/${documentId}`;
                await axios.delete(url);
                resolve(`Document with ID ${documentId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Service Template operations
    static createServiceTemplate(productId, serviceTemplateData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/service-templates`;
                const result = await axios.post(url, serviceTemplateData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateServiceTemplate(productId, templateId, updatedTemplateData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/service-templates/${templateId}`;
                await axios.put(url, updatedTemplateData);
                resolve(`Service template with ID ${templateId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteServiceTemplate(productId, templateId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/service-templates/${templateId}`;
                await axios.delete(url);
                resolve(`Service template with ID ${templateId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Certificate Type operations
    static createCertificateType(productId, certificateTypeData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/certificate-types`;
                const result = await axios.post(url, certificateTypeData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateCertificateType(productId, certificateTypeId, updatedCertificateTypeData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/certificate-types/${certificateTypeId}`;
                await axios.put(url, updatedCertificateTypeData);
                resolve(`Certificate type with ID ${certificateTypeId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteCertificateType(productId, certificateTypeId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/certificate-types/${certificateTypeId}`;
                await axios.delete(url);
                resolve(`Certificate type with ID ${certificateTypeId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Shipping Details operations
    static createShippingDetail(productId, shippingDetailData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/shipping-details`;
                const result = await axios.post(url, shippingDetailData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateShippingDetail(productId, shippingDetailId, updatedShippingDetailData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/shipping-details/${shippingDetailId}`;
                await axios.put(url, updatedShippingDetailData);
                resolve(`Shipping detail with ID ${shippingDetailId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteShippingDetail(productId, shippingDetailId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/shipping-details/${shippingDetailId}`;
                await axios.delete(url);
                resolve(`Shipping detail with ID ${shippingDetailId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Supplier Details operations
    static createSupplierDetail(productId, supplierDetailData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/supplier-details`;
                const result = await axios.post(url, supplierDetailData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateSupplierDetail(productId, supplierDetailId, updatedSupplierDetailData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/supplier-details/${supplierDetailId}`;
                await axios.put(url, updatedSupplierDetailData);
                resolve(`Supplier detail with ID ${supplierDetailId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteSupplierDetail(productId, supplierDetailId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/supplier-details/${supplierDetailId}`;
                await axios.delete(url);
                resolve(`Supplier detail with ID ${supplierDetailId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Bill of Material operations
    static createBillOfMaterial(productId, bomData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/bill-of-materials`;
                const result = await axios.post(url, bomData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateBillOfMaterial(productId, bomId, updatedBomData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/bill-of-materials/${bomId}`;
                await axios.put(url, updatedBomData);
                resolve(`Bill of material with ID ${bomId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteBillOfMaterial(productId, bomId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/bill-of-materials/${bomId}`;
                await axios.delete(url);
                resolve(`Bill of material with ID ${bomId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    //Compatible With operations

    static createCompatibleWith(productId, compatibleWithData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/compatible-with`;
                const result = await axios.post(url, compatibleWithData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static updateCompatibleWith(productId, compatibleWithId, updatedCompatibleWithData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/compatible-with/${compatibleWithId}`;
                await axios.put(url, updatedCompatibleWithData);
                resolve(`Compatible product with ID ${compatibleWithId} updated successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static deleteCompatibleWith(productId, compatibleWithId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/compatible-with/${compatibleWithId}`;
                await axios.delete(url);
                resolve(`Compatible product with ID ${compatibleWithId} deleted successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    // Unit Cost operations
    static createUnitCost(productId, unitCostData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/unit-cost-details`;
                const result = await axios.post(url, unitCostData);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }


    static updateUnitCost(productId, unitCostData) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/unit-cost-details`;
                await axios.put(url, unitCostData);
                resolve(`Unit cost updated successfully for product ID ${productId}`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static duplicateProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/duplicate`;
                const result = await axios.post(url);
                resolve(result.data);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static unarchiveProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/unarchive`;
                await axios.put(url);
                resolve(`Product with ID ${productId} unarchived successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static restoreProduct(productId) {
        return new Promise(async (resolve, reject) => {
            try {
                const url = `${productUrl}/${productId}/restore`;
                await axios.put(url);
                resolve(`Product with ID ${productId} restored successfully`);
            } catch (error) {
                reject(error.response);
            }
        });
    }

    static getExtFromFilename(filename) {
        if (!filename) return "";
        const parts = String(filename).split(".");
        if (parts.length < 2) return "";
        return parts.pop().toLowerCase();
    }

    static mimeToExt(mime) {
        if (!mime) return "";
        const m = String(mime).toLowerCase();
        const parts = m.split("/");
        if (parts.length === 2) return parts[1];
        return m;
    }

    // Make sure filename has an extension because backend uses path.extname(filename)
    static ensureFilenameHasExtension(filename, ext) {
        const hasExt = ProductServiceV2.getExtFromFilename(filename);
        if (hasExt) return filename;

        const cleanExt = String(ext || "").toLowerCase().replace(".", "");
        if (!cleanExt) return filename;

        return `${filename}.${cleanExt}`;
    }

    static normalizeUploadFile(file) {
        const rawFilename = file.filename || file.original_filename || file.file_name || file.name || "";
        const rawContentType = file.content_type || file.contentType || "";

        const extFromName = ProductServiceV2.getExtFromFilename(rawFilename);
        const extFromType = rawContentType.includes("/") ? ProductServiceV2.mimeToExt(rawContentType) : String(rawContentType).toLowerCase();

        const ext = extFromName || extFromType || "";
        const safeFilename = ProductServiceV2.ensureFilenameHasExtension(rawFilename, ext);

        return {
            filename: safeFilename,
            content_type: ext || extFromType,
            visibility: "Internal",
            category: "products",

            // document display name
            name: file.name || file.display_name || safeFilename,
            description: file.description,

            unique_id: file.unique_id,
            requires_thumbnail: file.requires_thumbnail !== undefined ? file.requires_thumbnail : false,

            document_role: file.document_role !== undefined ? file.document_role : "document",
        };
    }

    static uploadProductDocuments(files, productId, resourceRole = "document") {
        return new Promise(async (resolve, reject) => {
            try {
                const filesToUpload = (files || []).map(ProductServiceV2.normalizeUploadFile);

                const baseUrl = `${productUrl}/${productId}/documents`;
                const url = resourceRole ? `${baseUrl}?resource_role=${encodeURIComponent(resourceRole)}` : baseUrl;

                const result = await axios.post(url, filesToUpload);
                resolve(result.data);
            } catch (error) {
                console.error("Error uploading product documents:", error);
                reject(error.response || error);
            }
        });
    }

    static async getProductDocuments(productId, documentRole, isDefault, linkSourceOrOptions) {
        try {
            const baseUrl = `${productUrl}/${productId}/documents`;
            const queryParams = [];

            if (documentRole) queryParams.push(`document_role=${encodeURIComponent(documentRole)}`);
            if (isDefault !== undefined) queryParams.push(`is_default=${isDefault}`);

            if (linkSourceOrOptions) {
                if (typeof linkSourceOrOptions === "string") {
                    if (linkSourceOrOptions === "attached_existing") queryParams.push(`attached_existing_only=true`);
                    if (linkSourceOrOptions === "uploaded_new") queryParams.push(`uploaded_new_only=true`);
                } else {
                    const { resourceRole, attachedExistingOnly, uploadedNewOnly } = linkSourceOrOptions;
                    if (resourceRole) queryParams.push(`resource_role=${encodeURIComponent(resourceRole)}`);
                    if (attachedExistingOnly === true) queryParams.push(`attached_existing_only=true`);
                    if (uploadedNewOnly === true) queryParams.push(`uploaded_new_only=true`);
                }
            }

            const urlWithQuery = queryParams.length ? `${baseUrl}?${queryParams.join("&")}` : baseUrl;
            const result = await axios.get(urlWithQuery);
            return result.data;
        } catch (error) {
            console.error("Error fetching product documents:", error);
            throw error;
        }
    }

    static async updateProductDocument(productId, documentId, updateData) {
        try {
            const url = `${productUrl}/${productId}/documents/${documentId}`;
            const result = await axios.put(url, updateData);
            return result.data;
        } catch (error) {
            console.error("Error updating product document:", error);
            throw error;
        }
    }

    static async attachExistingProductDocument(productId, documentId, resourceRole = "document") {
        try {
            const url = `${productUrl}/${productId}/documents/${documentId}/resource-tags`;
            await axios.post(url, {
                resource_role: resourceRole,
                is_attached_existing: true,
            });
        } catch (error) {
            console.error("Error attaching existing product document:", error);
            throw error;
        }
    }

    static async detachProductDocument(productId, documentId, tagId) {
        try {
            const url = `${productUrl}/${productId}/documents/${documentId}/resource-tags/${tagId}`;
            await axios.delete(url);
        } catch (error) {
            console.error("Error detaching product document:", error);
            throw error;
        }
    }

    static async deleteProductDocuments(productId, documentIds) {
        try {
            const url = `${productUrl}/${productId}/documents`;
            await axios.delete(url, {
                data: documentIds,
                headers: { "Content-Type": "application/json" },
            });
        } catch (error) {
            console.error("Error deleting product documents:", error);
            throw error;
        }
    }



}

export default ProductServiceV2;
